<script setup lang="ts">
import PhIconButton from '@/components/ui/PhIconButton.vue'
import UICloseButton from '@/components/ui/UICloseButton.vue'
import UICollapseButton from '@/components/ui/UICollapseButton.vue'
import UIHeading from '@/components/ui/UIHeading.vue'
import useLanguage from '@/composables/useLanguage'
import { PhCaretLeft } from '@phosphor-icons/vue'

withDefaults(
  defineProps<{
    /** What text should be displayed in the header? */
    text: string
    /** Where is the header located (affects spacing)? */
    location?: keyof typeof locationMapping
    /** What variant should be used for the header coloration? */
    variant?: keyof typeof variantMapping
    /** What should the heading font size be? */
    headingSize?: 'serif' | 'serif-sm' | 'serif-xs'
    /** What HTML tag should the heading render as? */
    headingTag?: string
    /** What type of button should be included, if any? */
    btnVariant?: 'close' | 'collapse' | 'expand'
    /** Show a back button on the left, centring the title (emits `back`). */
    showBackButton?: boolean
    /** What should the aria label be for the button (if applicable)? */
    buttonAriaLabel?: string
  }>(),
  {
    location: 'modal',
    variant: 'default',
    headingSize: 'serif',
    headingTag: 'h4'
  }
)

defineEmits<{
  (e: 'btnClicked'): void
  (e: 'back'): void
}>()

const i18n = useLanguage('UIHeader')

const locationMapping = {
  modal: 'p-padding-panel-container-horz sm:pt-padding-panel-container-vert',
  panel: 'px-padding-panel-container-horz pt-4',
  mobilePanel: 'px-padding-panel-container-horz pt-5',
  legendCollapsed:
    'px-padding-default py-stack-tight sm:px-padding-panel-container-horz sm:py-2'
}

const variantMapping = {
  default: {
    container: '',
    textColor: 'text-default'
  },
  warn: {
    container: 'bg-warning',
    textColor: 'text-emphasis'
  },
  destructive: {
    container: 'bg-error',
    textColor: 'text-inverse'
  }
}
</script>

<template>
  <header
    class="w-full sm:rounded-t flex items-center justify-between space-x-2"
    :class="[locationMapping[location], variantMapping[variant].container]"
  >
    <PhIconButton
      v-if="showBackButton"
      :tooltip-text="i18n.back"
      tooltip-direction="bottom"
      tooltip-alignment="center"
      @click="$emit('back')"
    >
      <PhCaretLeft size="24" />
    </PhIconButton>
    <UIHeading
      :tag="headingTag"
      :variant="headingSize"
      :text="text"
      :class="[
        variantMapping[variant].textColor,
        showBackButton ? 'flex-1 text-center' : 'text-center sm:text-left'
      ]"
    ></UIHeading>
    <UICloseButton
      v-if="btnVariant === 'close'"
      tool-tip-direction="bottom"
      :aria-label="buttonAriaLabel"
      @close="$emit('btnClicked')"
    />
    <UICollapseButton
      v-else-if="btnVariant"
      :variant="btnVariant"
      :aria-label="buttonAriaLabel"
      @click="$emit('btnClicked')"
    />
  </header>
</template>
